import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AccessDeniedNotice } from '../../../shared/AccessDeniedNotice';
import { RetryableError } from '../../../shared/RetryableError';
import { bdErrorMessage, isForbidden } from '../bdErrors';
import { DEDUCTION_CATEGORY_CODES, DEDUCTION_CATEGORY_LABELS, deductionCategoryPath } from '../categories';
import { BulkAssignDialog } from '../components/BulkAssignDialog';
import { formatDate, formatDateTime } from '../dates';
import { deductionsGateway } from '../index';
import { loadAllDefinitions } from '../loaders';
import type { BulkAssignmentResult, DeductionDefinition, DeductionElection, DeductionSummary } from '../types';
import '../../job-roles/job-roles.css';
import '../benefits-deductions.css';

type SummaryState = { kind: 'loading' } | { kind: 'ready'; summary: DeductionSummary } | { kind: 'forbidden' } | { kind: 'error'; message: string };
type AssignedState = { kind: 'loading' } | { kind: 'ready'; elections: DeductionElection[]; definitions: DeductionDefinition[] } | { kind: 'unavailable' };

/**
 * People > Deductions > Deductions Overview. The definition counts come from Payroll Configuration (the summary); who has which deduction is composed here
 * from the company-wide active elections in Compensation and the definition list, so "employees with deductions" and "assigned" are counted from real
 * elections, not guessed. If the elections cannot be read the page says so instead of showing zero.
 */
export function DeductionsOverviewPage() {
  const [summary, setSummary] = useState<SummaryState>({ kind: 'loading' });
  const [assigned, setAssigned] = useState<AssignedState>({ kind: 'loading' });
  const [nonce, setNonce] = useState(0);
  const [assigning, setAssigning] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [readOnly, setReadOnly] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    deductionsGateway.getSummary(controller.signal)
      .then((s) => { if (!controller.signal.aborted) setSummary({ kind: 'ready', summary: s }); })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setSummary(isForbidden(err) ? { kind: 'forbidden' } : { kind: 'error', message: bdErrorMessage(err, 'Could not load the deductions overview.', 'deduction') });
      });
    Promise.all([deductionsGateway.listCompanyElections(controller.signal), loadAllDefinitions()])
      .then(([elections, definitions]) => { if (!controller.signal.aborted) setAssigned({ kind: 'ready', elections, definitions }); })
      .catch(() => { if (!controller.signal.aborted) setAssigned({ kind: 'unavailable' }); });
    return () => controller.abort();
  }, [nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const markReadOnly = useCallback(() => setReadOnly(true), []);

  const stats = useMemo(() => {
    if (assigned.kind !== 'ready') return null;
    const employees = new Set(assigned.elections.map((e) => e.employeeId));
    const categoryOf = (definitionId: string) => assigned.definitions.find((d) => d.id === definitionId)?.categoryCode ?? null;
    const byCategory = new Map<string, number>();
    for (const e of assigned.elections) {
      const category = categoryOf(e.deductionCodeId);
      if (category) byCategory.set(category, (byCategory.get(category) ?? 0) + 1);
    }
    return { employeesWithDeductions: employees.size, assignedCount: assigned.elections.length, byCategory };
  }, [assigned]);

  const forbidden = summary.kind === 'forbidden';
  const canWrite = !readOnly && !forbidden;
  const data = summary.kind === 'ready' ? summary.summary : null;
  const assignable = assigned.kind === 'ready' ? assigned.definitions.filter((d) => d.categoryCode !== 'garnishments-levies' && d.categoryCode !== 'taxes-statutory') : [];

  function done(result: BulkAssignmentResult, def: DeductionDefinition) {
    setNotice(`${def.name} was ${result.replayed ? 'already assigned' : 'assigned'} to ${result.createdCount} ${result.createdCount === 1 ? 'employee' : 'employees'}.`);
    reload();
  }

  return (
    <section className="card card--wide jr-page bd-page">
      <div className="section-header">
        <h2 ref={headingRef} tabIndex={-1}>Deductions Overview</h2>
      </div>
      <p className="muted bd-lead">The deductions your company has set up, who has them, and what needs attention. Taxes and statutory deductions are calculated by the system.</p>
      {canWrite && assigned.kind === 'ready' && (
        <div className="bd-actions-row">
          <button type="button" className="jr-btn jr-btn--primary" onClick={() => setAssigning(true)}>Assign a Deduction to Employees</button>
        </div>
      )}
      {readOnly && (
        <p className="jr-readonly" role="status" data-testid="read-only-notice">
          <span aria-hidden="true">{'🔒'} </span>You can view deductions but not change them. Ask a company administrator if you need to assign a deduction.
        </p>
      )}
      {notice && (
        <p className="jr-notice jr-notice--ok" role="status">
          <span aria-hidden="true">{'✓'} </span>{notice}{' '}
          <button type="button" className="link" onClick={() => { setNotice(null); headingRef.current?.focus(); }}>Dismiss</button>
        </p>
      )}

      {summary.kind === 'loading' && <p className="muted" role="status">Loading the overview{'…'}</p>}
      {summary.kind === 'error' && <RetryableError message={summary.message} onRetry={() => { setSummary({ kind: 'loading' }); reload(); }} />}
      {summary.kind === 'forbidden' && <AccessDeniedNotice requiredPermissionLabel="permission to view deductions (deductioncodes.read)" />}

      {data && (
        <>
          <ul className="bd-metrics" aria-label="Deduction totals">
            <li className="bd-metric"><p className="bd-metric__label">Active deductions</p><p className="bd-metric__value" data-testid="metric-active">{data.totalActive}</p></li>
            <li className="bd-metric">
              <p className="bd-metric__label">Employees with deductions</p>
              <p className="bd-metric__value" data-testid="metric-employees">{stats ? stats.employeesWithDeductions : assigned.kind === 'loading' ? '…' : 'Unavailable'}</p>
              <p className="bd-metric__hint">With at least one active assignment</p>
            </li>
            <li className="bd-metric">
              <p className="bd-metric__label">Assigned deductions</p>
              <p className="bd-metric__value" data-testid="metric-assigned">{stats ? stats.assignedCount : assigned.kind === 'loading' ? '…' : 'Unavailable'}</p>
              <p className="bd-metric__hint">Active assignments</p>
            </li>
            <li className="bd-metric"><p className="bd-metric__label">Awaiting configuration</p><p className="bd-metric__value" data-testid="metric-awaiting">{data.awaitingConfiguration}</p><p className="bd-metric__hint">Need tax or rule review</p></li>
            <li className="bd-metric"><p className="bd-metric__label">Ending soon</p><p className="bd-metric__value" data-testid="metric-ending">{data.endingSoon.length}</p><p className="bd-metric__hint">Within 30 days</p></li>
          </ul>
          {assigned.kind === 'unavailable' && <p className="bd-notice bd-notice--warn" role="status">The assignments could not be read, so the employee counts are not shown.</p>}

          <div className="bd-columns">
            <section className="bd-panel" aria-labelledby="bd-dov-categories">
              <h3 id="bd-dov-categories">By category</h3>
              <div className="jr-table-scroll" role="region" aria-label="Deduction categories table" tabIndex={0}>
                <table className="jr-table">
                  <caption className="jr-sr-only">Deductions by category</caption>
                  <thead><tr><th scope="col">Category</th><th scope="col" className="num">Active</th><th scope="col" className="num">Total</th><th scope="col" className="num">Assigned</th></tr></thead>
                  <tbody>
                    {DEDUCTION_CATEGORY_CODES.map((code) => {
                      const row = data.byCategory.find((c) => c.categoryCode === code);
                      const statutory = code === 'taxes-statutory';
                      return (
                        <tr key={code}>
                          <th scope="row"><Link to={deductionCategoryPath(code)}>{DEDUCTION_CATEGORY_LABELS[code]}</Link></th>
                          <td className="num">{statutory ? 'System' : row?.active ?? 0}</td>
                          <td className="num">{statutory ? 'System' : row?.total ?? 0}</td>
                          <td className="num">{statutory ? 'System' : stats ? stats.byCategory.get(code) ?? 0 : '…'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
            <section className="bd-panel" aria-labelledby="bd-dov-ending">
              <h3 id="bd-dov-ending">Ending soon</h3>
              {data.endingSoon.length === 0 ? <p className="muted">No active deduction ends in the next 30 days.</p> : (
                <ul className="bd-plain-list">
                  {data.endingSoon.map((d) => <li key={d.id}>{d.name}<span className="bd-when">Ends {formatDate(d.effectiveEndDate)}</span></li>)}
                </ul>
              )}
            </section>
            <section className="bd-panel" aria-labelledby="bd-dov-recent">
              <h3 id="bd-dov-recent">Recent changes</h3>
              {data.recentChanges.length === 0 ? <p className="muted">No changes yet.</p> : (
                <ul className="bd-plain-list">
                  {data.recentChanges.map((c, i) => <li key={`${c.occurredAtUtc}-${i}`}>{c.summary}<span className="bd-when">{formatDateTime(c.occurredAtUtc)}</span></li>)}
                </ul>
              )}
            </section>
          </div>
        </>
      )}

      {assigning && <BulkAssignDialog definitions={assignable} onClose={() => setAssigning(false)} onDone={done} onForbidden={markReadOnly} />}
    </section>
  );
}
